import { useState, FormEvent } from 'react';
import { Download, Sparkles, Send, Settings, BarChart2, Table, Database, Undo2, Redo2, Check } from 'lucide-react';
import { usePrototype1 } from '../Prototype1Context';
import { SECTIONS, SectionStatus } from '../prototype1Data';
import DraftBody from '../../_shared/DraftBody';

const STATUS_BADGE: Record<SectionStatus, { label: string; className: string }> = {
  complete: {
    label: 'Complete',
    className: 'text-white bg-[#16a34a] border-transparent',
  },
  'in-progress': {
    label: 'In Progress',
    className: 'text-[#737373] bg-[#f5f5f5] border-[#e5e5e5]',
  },
  'not-started': {
    label: 'Not Started',
    className: 'text-[#737373] bg-[#f5f5f5] border-[#e5e5e5]',
  },
};

const Screen06SectionEditorAiDraft = () => {
  const {
    activeSectionId,
    chatHistoriesBySection,
    isChatPending,
    sendChat,
    goToScreen,
    drafts,
    sectionStatuses,
    approveSection,
    undoDraft,
    redoDraft,
    canUndoDraft,
    canRedoDraft,
  } = usePrototype1();
  const [input, setInput] = useState('');
  const section = SECTIONS.find((s) => s.id === activeSectionId) ?? SECTIONS[0];
  const sectionId = section.id;
  const status = sectionStatuses[sectionId] ?? section.status;
  const chatHistory = chatHistoriesBySection[sectionId] ?? [];
  const draft = drafts[sectionId] ?? '';
  const canUndo = canUndoDraft(sectionId);
  const canRedo = canRedoDraft(sectionId);
  const showApprove = canUndo;
  const statusBadge = STATUS_BADGE[status];

  const handleApprove = () => {
    approveSection(sectionId);
    goToScreen('05-dashboard-report-overview');
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isChatPending) return;
    const message = input;
    setInput('');
    await sendChat(message, {
      intent: 'edit-current-draft',
      active_section_id: sectionId,
      current_draft: draft,
      ui_state: { panel: 'chat-open' },
      response_hint: { shape: 'rewrite-paragraph' },
    });
  };

  const handleQuickAction = async (label: string, intent: 'propose-chart' | 'qa') => {
    await sendChat(label, {
      intent,
      active_section_id: sectionId,
      current_draft: draft,
      ui_state: { panel: 'chat-open', last_user_action: `quick action: ${label}` },
    });
  };

  return (
    <div className="flex h-full w-full bg-[#fafafa] font-['Inter']">
      <div className="flex flex-col flex-1 min-w-0">
        <div className="flex items-center justify-between px-6 h-14 border-b border-[#e5e5e5] shrink-0">
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={() => goToScreen('05-dashboard-report-overview')}
              className="text-[#737373] hover:underline"
            >
              Issaquah School District
            </button>
            <span className="text-[#737373]">/</span>
            <span className="text-[#737373]">Class of 2025</span>
            <span className="text-[#737373]">/</span>
            <span className="font-medium text-[#0a0a0a]">{section.title}</span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center px-2.5 py-0.5 text-xs font-medium rounded-full border ${statusBadge.className}`}
            >
              {statusBadge.label}
            </span>
            <button
              type="button"
              className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium border border-[#e5e5e5] rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              Export Draft
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          <div className="flex flex-col flex-1 min-w-0 bg-[#fafafa]">
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#e5e5e5] shrink-0">
              <div className="flex flex-col gap-1">
                <span className="text-base font-semibold text-[#0a0a0a]">
                  Section {section.index}: {section.title}
                </span>
                <span className="text-[13px] text-[#737373]">
                  {status === 'complete'
                    ? 'Approved section — edit below or use the AI assistant'
                    : 'Draft content generated by AI — review and edit below'}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => undoDraft(sectionId)}
                  disabled={!canUndo}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#737373] hover:bg-[#f5f5f5] rounded-md transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                >
                  <Undo2 className="w-4 h-4" />
                  Undo
                </button>
                <button
                  type="button"
                  onClick={() => redoDraft(sectionId)}
                  disabled={!canRedo}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-[#737373] hover:bg-[#f5f5f5] rounded-md transition-colors disabled:opacity-40 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                >
                  <Redo2 className="w-4 h-4" />
                  Redo
                </button>
                {showApprove && (
                  <button
                    type="button"
                    onClick={handleApprove}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] font-medium text-white bg-[#171717] hover:bg-[#2a2a2a] rounded-md transition-colors"
                  >
                    <Check className="w-4 h-4" />
                    Approve Section
                  </button>
                )}
              </div>
            </div>

            <div className="flex flex-col gap-5 p-8 overflow-y-auto flex-1">
              <h1 className="text-[22px] font-bold text-[#0a0a0a]">{section.title}</h1>
              <DraftBody text={draft} />
              {sectionId === 'postsecondary-readiness' && (
                <div className="flex flex-col items-center justify-center h-[160px] rounded-lg bg-[#f5f5f5] border border-[#e5e5e5] gap-2">
                  <BarChart2 className="w-8 h-8 text-[#a3a3a3]" />
                  <span className="text-[13px] font-medium text-[#737373]">
                    Figure 2.3 — Postsecondary Readiness by Route
                  </span>
                  <span className="text-[11px] text-[#737373]">Auto-generated visualization will appear here</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col w-[420px] shrink-0 border-l border-[#e5e5e5] bg-[#fafafa]">
            <div className="flex items-center justify-between px-4 h-12 border-b border-[#e5e5e5] shrink-0">
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-6 h-6 rounded-full bg-[#171717]">
                  <Sparkles className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-[13px] font-semibold text-[#0a0a0a]">AI Draft Assistant</span>
              </div>
              <Settings className="w-4 h-4 text-[#737373]" />
            </div>

            <div className="flex flex-col gap-4 flex-1 overflow-y-auto p-4">
              {chatHistory.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'items-start gap-2'}`}>
                  {m.role === 'assistant' && (
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-[#171717] shrink-0 mt-0.5">
                      <Sparkles className="w-3 h-3 text-white" />
                    </div>
                  )}
                  <div
                    className={`rounded-lg px-3 py-2.5 ${
                      m.role === 'user'
                        ? 'bg-[#171717] text-white max-w-[80%]'
                        : 'bg-[#f5f5f5] border border-[#e5e5e5] text-[#0a0a0a] flex-1'
                    }`}
                  >
                    {m.role === 'user' ? (
                      <p className="text-[13px] leading-[1.5]">{m.content}</p>
                    ) : (
                      <DraftBody text={m.content} />
                    )}
                  </div>
                </div>
              ))}
              {isChatPending && <span className="text-xs text-[#737373]">AI is typing…</span>}
            </div>

            <div className="flex flex-col gap-2.5 px-4 py-3 border-t border-[#e5e5e5] shrink-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <button
                  type="button"
                  onClick={() => handleQuickAction('Add chart', 'propose-chart')}
                  disabled={isChatPending}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-md bg-white hover:bg-[#f5f5f5] transition-colors disabled:opacity-50"
                >
                  <BarChart2 className="w-3.5 h-3.5" />
                  Add chart
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickAction('Add table', 'propose-chart')}
                  disabled={isChatPending}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-md bg-white hover:bg-[#f5f5f5] transition-colors disabled:opacity-50"
                >
                  <Table className="w-3.5 h-3.5" />
                  Add table
                </button>
                <button
                  type="button"
                  onClick={() => handleQuickAction('Query data', 'qa')}
                  disabled={isChatPending}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-[12px] font-medium text-[#0a0a0a] border border-[#e5e5e5] rounded-md bg-white hover:bg-[#f5f5f5] transition-colors disabled:opacity-50"
                >
                  <Database className="w-3.5 h-3.5" />
                  Query data
                </button>
              </div>
              <form onSubmit={handleSubmit} className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ask or instruct the AI..."
                  disabled={isChatPending}
                  className="flex-1 h-10 px-3 rounded-lg bg-[#fafafa] border border-[#e5e5e5] text-[13px] text-[#0a0a0a] outline-none"
                />
                <button
                  type="submit"
                  disabled={isChatPending || !input.trim()}
                  className="flex items-center justify-center w-9 h-9 rounded-lg bg-[#171717] shrink-0 hover:bg-[#2a2a2a] transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4 text-white" />
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Screen06SectionEditorAiDraft;
